import { createContext, useCallback, useContext, useLayoutEffect, useRef, type CSSProperties, type ReactNode } from "react";
import { BUXUS_ER_FAMILY_PRIMARY_HEX, isRegistrationWebViewHost } from "@buxus/shared";
import { dismissHtmlBootSplash } from "../platform/ui/bootSplash";
import { scrollRegistrationToTop } from "./registrationScroll";

const RegistrationScrollContext = createContext<(() => void) | null>(null);

/** Scroll the registration shell to top (main column + window). */
export function useRegistrationScrollToTop(): () => void {
  const fromContext = useContext(RegistrationScrollContext);
  return fromContext ?? (() => scrollRegistrationToTop());
}

const registrationShellStyle = {
  "--buxus-er-primary": BUXUS_ER_FAMILY_PRIMARY_HEX,
} as CSSProperties;

export type RegistrationHostKind = "unknown" | "browser" | "webview";

/** Detect embedded React Native WebView (`window.isWebView` or `ReactNativeWebView`). */
export function detectRegistrationHostKind(): RegistrationHostKind {
  if (typeof window === "undefined") return "unknown";
  return isRegistrationWebViewHost() ? "webview" : "browser";
}

/** Red banner for standalone browser only; mobile shell provides its own chrome. */
export function RegHeader({
  hostKind,
  title = 'Register',
}: {
  hostKind: RegistrationHostKind
  title?: string
}) {
  if (hostKind === 'webview') return null
  return (
    <header className="reg-header">
      <h1>{title}</h1>
    </header>
  )
}

/** Legacy-style copyright bar for standalone browser only. */
export function RegFooter({ hostKind }: { hostKind: RegistrationHostKind }) {
  if (hostKind === "webview") return null;
  return (
    <footer className="reg-footer" aria-label="Site footer">
      Buxus © {new Date().getFullYear()}
    </footer>
  );
}

export function RegistrationShell({
  hostKind,
  title = 'Register',
  children,
}: {
  hostKind: RegistrationHostKind
  title?: string
  children: ReactNode
}) {
  const mainRef = useRef<HTMLElement>(null)
  const scrollToTop = useCallback(() => {
    scrollRegistrationToTop(mainRef.current)
  }, [])

  useLayoutEffect(() => {
    dismissHtmlBootSplash()
  }, [])

  return (
    <RegistrationScrollContext.Provider value={scrollToTop}>
      <div className={`reg-shell reg-shell--host-${hostKind}`} style={registrationShellStyle}>
        <RegHeader hostKind={hostKind} title={title} />
        <main ref={mainRef} className="reg-main">
          {children}
        </main>
        <RegFooter hostKind={hostKind} />
      </div>
    </RegistrationScrollContext.Provider>
  );
}
